/**
 * ConfigureProvider for Adobe I/O Events Provider Configuration
 *
 * <license header>
 */

import {
  EventConfigurationService,
  EventProviderService,
  type EventProvider,
} from '@adobe-commerce/aio-services-kit';
import AdobeCommerceClient from '../../../commerce/adobe-commerce-client';
import CustomLogger from '../../../framework/custom-logger';
import type { Provider } from '../../../io-events/provider/types';
import type { WorkspaceConfig } from '../types';
import type { ConfigureProviderResult } from './types';

/**
 * ConfigureProvider for Adobe I/O Events Provider Setup
 *
 * This class handles the configuration and setup of Adobe I/O Events providers
 * for Adobe Commerce integration.
 *
 * @example
 * ```typescript
 * const configureProvider = new ConfigureProvider(
 *   adobeCommerceClient,
 *   'merchant-id-123',
 *   'environment-id-456',
 *   logger
 * );
 * const result = await configureProvider.execute(provider, workspaceConfig);
 * ```
 */
class ConfigureProvider {
  /** Adobe Commerce client instance */
  private readonly adobeCommerceClient: AdobeCommerceClient;

  /** Merchant ID for Adobe Commerce */
  private readonly merchantId: string;

  /** Environment ID for Adobe Commerce */
  private readonly environmentId: string;

  /** CustomLogger instance for logging operations */
  private readonly customLogger: CustomLogger;

  /** EventProviderService instance for provider operations */
  private readonly eventProviderService: EventProviderService;

  /** EventConfigurationService instance for configuration operations */
  private readonly eventConfigurationService: EventConfigurationService;

  /**
   * Constructor for ConfigureProvider
   *
   * @param adobeCommerceClient - Adobe Commerce client instance
   * @param merchantId - Merchant ID for Adobe Commerce
   * @param environmentId - Environment ID for Adobe Commerce
   * @param logger - Optional logger instance for logging operations
   * @throws {Error} When required parameters are missing or invalid
   * @example
   * ```typescript
   * const configureProvider = new ConfigureProvider(
   *   adobeCommerceClient,
   *   'merchant-id-123',
   *   'environment-id-456',
   *   logger
   * );
   * ```
   */
  constructor(
    adobeCommerceClient: AdobeCommerceClient,
    merchantId: string,
    environmentId: string,
    logger: any = null
  ) {
    // Validate required parameters
    if (!adobeCommerceClient) {
      throw new Error('Adobe Commerce client is required');
    }
    if (!merchantId || typeof merchantId !== 'string') {
      throw new Error('Valid merchant ID is required');
    }
    if (!environmentId || typeof environmentId !== 'string') {
      throw new Error('Valid environment ID is required');
    }

    this.adobeCommerceClient = adobeCommerceClient;
    this.merchantId = merchantId;
    this.environmentId = environmentId;
    this.customLogger = new CustomLogger(logger);
    this.eventProviderService = new EventProviderService(this.adobeCommerceClient);
    this.eventConfigurationService = new EventConfigurationService(this.adobeCommerceClient);
  }

  /**
   * Execute provider configuration
   *
   * This method handles the provider configuration process for Adobe I/O Events.
   *
   * @param provider - Provider configuration for Adobe I/O Events
   * @param workspaceConfig - Workspace configuration settings
   * @returns A promise that resolves with the configuration result
   * @throws {Error} If provider configuration fails
   * @example
   * ```typescript
   * const result = await configureProvider.execute(provider, workspaceConfig);
   * if (result.success) {
   *   console.log('Provider configured successfully');
   * }
   * ```
   */
  async execute(
    provider: Provider,
    workspaceConfig: WorkspaceConfig
  ): Promise<ConfigureProviderResult> {
    this.customLogger.debug('[DEBUG] Starting provider configuration');

    try {
      // Validate input parameters
      this.validateConfigureParams(provider, workspaceConfig);

      // Get current event providers
      this.customLogger.debug('[FETCH] Fetching current event providers');
      const eventProviderResult = await this.eventProviderService.list();

      if (!eventProviderResult.success) {
        const errorMsg = `Failed to fetch event providers: ${eventProviderResult.error}`;
        this.customLogger.error(`[ERROR] ${errorMsg}`);
        return {
          success: false,
          error: errorMsg,
          details: eventProviderResult,
        };
      }

      // Check if default workspace is empty and update if needed
      const workspaceUpdateResult = await this.updateWorkspaceIfEmpty(
        eventProviderResult.data,
        workspaceConfig
      );
      if (!workspaceUpdateResult.success) {
        return workspaceUpdateResult;
      }

      // Check if the provider is already added
      const providerList = Array.isArray(eventProviderResult.data) ? eventProviderResult.data : [];
      const existingProvider = providerList.find(
        (existingProvider: any) => existingProvider.provider_id === provider.id
      );

      if (existingProvider) {
        this.customLogger.info(`[SKIP] Provider ${provider.id} is already configured`);
        return {
          success: true,
          provider: existingProvider,
        };
      } else {
        this.customLogger.debug(
          `[DEBUG] Provider ${provider.id} is not yet configured - creating new provider...`
        );

        const createResult = await this.createNewProvider(provider, workspaceConfig);
        if (!createResult.success) {
          return createResult;
        }

        this.customLogger.info(`[CREATE] Event provider ${provider.id} created successfully`);
        return {
          success: true,
          provider: createResult.provider!,
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.customLogger.error(`[ERROR] Provider configuration failed: ${errorMessage}`);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Creates a new event provider
   *
   * @param provider - Provider configuration object
   * @param workspaceConfig - Workspace configuration object
   * @returns Creation result with success status and provider data
   * @private
   */
  private async createNewProvider(
    provider: Provider,
    workspaceConfig: WorkspaceConfig
  ): Promise<ConfigureProviderResult> {
    const providerData = {
      provider_id: provider.id,
      instance_id: provider.instance_id!, // Already validated in validateConfigureParams
      label: provider.label,
      description: provider.description,
      workspace_configuration: JSON.stringify(workspaceConfig),
    };

    this.customLogger.debug(
      `[DEBUG] Creating event provider:\n${JSON.stringify(providerData, null, 2)}`
    );

    const createResult = await this.eventProviderService.create(providerData);

    if (!createResult.success) {
      const errorMsg = `Failed to create event provider: ${createResult.error}`;
      this.customLogger.error(`[ERROR] ${errorMsg}`);
      return {
        success: false,
        error: errorMsg,
        details: createResult,
      };
    }

    return {
      success: true,
      provider: createResult.data as EventProvider,
    };
  }

  /**
   * Updates workspace configuration if the default workspace is empty
   *
   * @param existingProviders - Array of existing providers
   * @param workspaceConfig - Workspace configuration object
   * @returns Update result with success status
   * @private
   */
  private async updateWorkspaceIfEmpty(
    existingProviders: any,
    workspaceConfig: WorkspaceConfig
  ): Promise<ConfigureProviderResult> {
    const providerArray = Array.isArray(existingProviders) ? existingProviders : [];
    const isDefaultWorkspaceEmpty =
      providerArray.length === 0 ||
      providerArray.every(
        (item: any) => !item.workspace_configuration || item.workspace_configuration === ''
      );

    if (isDefaultWorkspaceEmpty) {
      this.customLogger.debug('[DEBUG] Default workspace is empty, updating configuration...');

      const updateConfigPayload = {
        enabled: true,
        merchant_id: this.merchantId,
        environment_id: this.environmentId,
        workspace_configuration: JSON.stringify(workspaceConfig),
      };

      this.customLogger.debug(
        `[DEBUG] Updating configuration with payload:\n${JSON.stringify(updateConfigPayload, null, 2)}`
      );

      const updateResult = await this.eventConfigurationService.update(updateConfigPayload);

      if (!updateResult.success) {
        const errorMsg = `Failed to update configuration: ${updateResult.error}`;
        this.customLogger.error(`[ERROR] ${errorMsg}`);
        return {
          success: false,
          error: errorMsg,
          details: updateResult,
        };
      }

      this.customLogger.info('[UPDATE] Configuration updated successfully');
    }

    return { success: true };
  }

  /**
   * Validate input parameters for configure method
   *
   * @param provider - Provider configuration for Adobe I/O Events
   * @param workspaceConfig - Workspace configuration settings
   * @throws {Error} If validation fails
   * @private
   */
  private validateConfigureParams(provider: Provider, workspaceConfig: WorkspaceConfig): void {
    if (!provider || typeof provider !== 'object') {
      throw new Error('Provider configuration object is required');
    }

    const requiredProviderFields: (keyof Provider)[] = [
      'id',
      'instance_id',
      'label',
      'description',
    ];
    for (const field of requiredProviderFields) {
      if (!provider[field] || typeof provider[field] !== 'string') {
        throw new Error(`Provider ${field} is required and must be a string`);
      }
    }

    if (!workspaceConfig || typeof workspaceConfig !== 'object') {
      throw new Error('Workspace configuration object is required');
    }

    this.customLogger.debug(`[DEBUG] Validated provider: ${provider.label} (${provider.id})`);
  }
}

export default ConfigureProvider;
